import { apiClient } from '@/lib/api/apiClient';

export interface Category {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
  questionCount?: number;
  parentCategoryId?: string;
  isEnabled?: boolean;
  displayOrder?: number;
}

// Fallback categories if API fails
const FALLBACK_CATEGORIES: Category[] = [
  {
    id: '1',
    name: 'تاريخ',
    description: 'أسئلة عن التاريخ العربي والعالمي',
    questionCount: 150,
    imageUrl: '/images/categories/history.png'
  },
  {
    id: '2',
    name: 'جغرافيا',
    description: 'أسئلة عن الجغرافيا والبلدان',
    questionCount: 200,
    imageUrl: '/images/categories/geography.png'
  },
  {
    id: '3',
    name: 'علوم',
    description: 'أسئلة عن العلوم الطبيعية',
    questionCount: 180,
    imageUrl: '/images/categories/science.png'
  },
  {
    id: '4',
    name: 'رياضة',
    description: 'أسئلة عن الرياضة والألعاب',
    questionCount: 120,
    imageUrl: '/images/categories/sports.png'
  },
  {
    id: '5',
    name: 'أدب',
    description: 'أسئلة عن الأدب والشعر',
    questionCount: 100,
    imageUrl: '/images/categories/literature.png'
  },
  {
    id: '6',
    name: 'فنون',
    description: 'أسئلة عن الفنون والسينما',
    questionCount: 90,
    imageUrl: '/images/categories/arts.png'
  },
  {
    id: '7',
    name: 'دين',
    description: 'أسئلة دينية',
    questionCount: 160,
    imageUrl: '/images/categories/religion.png'
  },
  {
    id: '8',
    name: 'تقنية',
    description: 'أسئلة عن التكنولوجيا والحاسوب',
    questionCount: 110,
    imageUrl: '/images/categories/technology.png'
  }
];

export const playerCategoryService = {
  /**
   * Get all enabled categories from the backend
   */
  async getCategories(): Promise<Category[]> {
    try {
      const response = await apiClient.get('/api/categories');
      // API returns array directly
      const categories = Array.isArray(response.data) ? response.data : [];
      
      if (categories.length === 0) {
        console.warn('No categories returned from API, using fallback data');
        return FALLBACK_CATEGORIES;
      }
      
      return categories.map((cat: any) => ({
        id: cat.id,
        name: cat.name,
        imageUrl: cat.imageUrl,
        description: cat.description,
        questionCount: cat.questionCount,
        parentCategoryId: cat.parentCategoryId,
        isEnabled: cat.isEnabled,
        displayOrder: cat.displayOrder,
      }));
    } catch (error) {
      console.warn('Failed to fetch categories from API, using fallback data', error);
      return FALLBACK_CATEGORIES;
    }
  },

  /**
   * Get a single category by ID
   */
  async getCategoryById(id: string): Promise<Category | null> {
    const categories = await this.getCategories();
    return categories.find(c => c.id === id) || null;
  },

  /**
   * Get featured categories for the landing page
   */
  async getFeaturedCategories(): Promise<Category[]> {
    const categories = await this.getCategories();
    return categories.slice(0, 6);
  },

  /**
   * آخر N فئة مضافة من قاعدة البيانات (للعرض في "جديد هذا الأسبوع").
   * مرتبة من الأحدث - تتحدث تلقائياً عند إضافة فئات من الأدمن.
   */
  async getLatestCategories(count: number = 3): Promise<Category[]> {
    try {
      const response = await apiClient.get('/api/categories/latest', { params: { count } });
      const list = Array.isArray(response.data) ? response.data : [];
      return list.map((cat: any) => ({
        id: cat.id,
        name: cat.name,
        imageUrl: cat.imageUrl,
        description: cat.description,
        questionCount: cat.questionCount,
        parentCategoryId: cat.parentCategoryId,
        isEnabled: cat.isEnabled,
        displayOrder: cat.displayOrder,
      }));
    } catch (error) {
      console.warn('Failed to fetch latest categories, using first N from all', error);
      const categories = await this.getCategories();
      return categories.slice(0, count);
    }
  },

  /**
   * Get fallback categories (for offline/error scenarios)
   */
  getFallbackCategories(): Category[] {
    return FALLBACK_CATEGORIES;
  }
};
